// ============================================
// Path: /frontend/src/pages/admin/SuperAdminPage.jsx
// Purpose: Super admin page for managing users and roles
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { usersAPI } from '../../api/users.api';
import { useToast } from '../../hooks/useToast';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Eye, Loader2, MoreHorizontal, CheckCircle, XCircle, UserCheck, Ban, UserX, UserPlus } from 'lucide-react';
import { ArrowUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Pagination } from '../../components/ui/Pagination';

// ========================================
// LOADING SKELETON COMPONENTS
// ========================================
const LoadingSkeleton = () => (
  <CardContent>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Full Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Joined On</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell><Skeleton className="h-5 w-48" /></TableCell>
            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded-md" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent>
);

const ApprovalsSkeleton = () => (
  <CardContent>
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between p-3 border rounded">
          <div className="space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-20" />
          </div>
        </div>
      ))}
    </div>
  </CardContent>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function SuperAdminPage() {
  console.log('🎨 [SuperAdminPage] Component mounted');

  const { toast } = useToast();

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingApprovals, setLoadingApprovals] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // ========================================
  // DATA FETCHING
  // ========================================
  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, sortBy, sortOrder, filterRole, filterStatus]);

  const fetchUsers = async () => {
    console.log('🔵 [SuperAdminPage] Fetching users...', {
      page: currentPage,
      sortBy,
      sortOrder,
      filterRole,
      filterStatus
    });
    setLoading(true);

    try {
      // Build query params
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder
      };

      // Add filters if not 'all'
      if (filterRole !== 'all') {
        params.role = filterRole;
      }
      if (filterStatus !== 'all') {
        params.accountStatus = filterStatus;
      }

      const data = await usersAPI.getAllUsers(params);
      console.log('✅ [SuperAdminPage] Users received:', data);
      
      setUsers(data.users || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalUsers(data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      console.error('❌ [SuperAdminPage] Fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    console.log('🔵 [SuperAdminPage] Fetching pending approvals...');
    setLoadingApprovals(true);

    try {
      const data = await usersAPI.getPendingApprovals();
      console.log('✅ [SuperAdminPage] Pending approvals received:', data);
      setPendingUsers(data.users || []);
    } catch (err) {
      console.error('❌ [SuperAdminPage] Fetch pending approvals error:', err);
    } finally {
      setLoadingApprovals(false);
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleApproveUser = async (userId) => {
    console.log('🔵 [SuperAdminPage] Approving user:', userId);
    setUpdatingId(userId);

    try {
      await usersAPI.approveUser(userId);
      console.log('✅ [SuperAdminPage] User approved successfully');

      // Remove from pending list
      setPendingUsers((prev) => prev.filter((user) => user.id !== userId));

      toast({
        title: 'Success',
        description: 'User account has been approved.',
      });

      // Refresh users list
      fetchUsers();
    } catch (err) {
      console.error('❌ [SuperAdminPage] Approve user error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not approve user account.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRejectUser = async () => {
    if (!selectedUser) return;

    console.log('🔵 [SuperAdminPage] Rejecting user:', selectedUser.id);
    setUpdatingId(selectedUser.id);

    try {
      await usersAPI.rejectUser(selectedUser.id, rejectionReason);
      console.log('✅ [SuperAdminPage] User rejected successfully');

      // Remove from pending list
      setPendingUsers((prev) => prev.filter((user) => user.id !== selectedUser.id));

      toast({
        title: 'User Rejected',
        description: 'User account has been rejected.',
      });

      // Close dialog and reset
      setRejectDialogOpen(false);
      setSelectedUser(null);
      setRejectionReason('');
    } catch (err) {
      console.error('❌ [SuperAdminPage] Reject user error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not reject user account.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const openRejectDialog = (user) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  const handleRoleChange = async (id, newRole) => {
    console.log('🔵 [SuperAdminPage] Changing role...', { id, newRole });
    setUpdatingId(id);

    try {
      await usersAPI.updateUserRole(id, newRole);
      console.log('✅ [SuperAdminPage] Role updated successfully');

      setUsers((prev) =>
        prev.map((user) => (user.id === id ? { ...user, role: newRole } : user))
      );

      toast({
        title: 'Success',
        description: `User role has been updated to ${newRole}.`,
      });
    } catch (err) {
      console.error('❌ [SuperAdminPage] Role update error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not update user role.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSuspendUser = async (user) => {
    console.log('🔵 [SuperAdminPage] Suspending user:', user.id);
    setUpdatingId(user.id);

    try {
      await usersAPI.updateAccountStatus(user.id, 'SUSPENDED');
      console.log('✅ [SuperAdminPage] User suspended successfully');

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, accountStatus: 'SUSPENDED' } : u))
      );

      toast({
        title: 'User Suspended',
        description: `${user.fullName}'s account has been suspended.`,
      });
    } catch (err) {
      console.error('❌ [SuperAdminPage] Suspend user error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not suspend user account.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBanUser = async (user) => {
    console.log('🔵 [SuperAdminPage] Banning user:', user.id);
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  const handleBanUserConfirm = async () => {
    if (!selectedUser) return;

    console.log('🔵 [SuperAdminPage] Banning user:', selectedUser.id);
    setUpdatingId(selectedUser.id);

    try {
      await usersAPI.rejectUser(selectedUser.id, rejectionReason || 'Banned by admin');
      console.log('✅ [SuperAdminPage] User banned successfully');

      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? { ...u, accountStatus: 'REJECTED' } : u))
      );

      toast({
        title: 'User Banned',
        description: `${selectedUser.fullName}'s account has been banned.`,
      });

      setRejectDialogOpen(false);
      setSelectedUser(null);
      setRejectionReason('');
    } catch (err) {
      console.error('❌ [SuperAdminPage] Ban user error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not ban user account.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleReactivateUser = async (user) => {
    console.log('🔵 [SuperAdminPage] Reactivating user:', user.id);
    setUpdatingId(user.id);

    try {
      await usersAPI.updateAccountStatus(user.id, 'APPROVED');
      console.log('✅ [SuperAdminPage] User reactivated successfully');

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, accountStatus: 'APPROVED' } : u))
      );

      toast({
        title: 'User Reactivated',
        description: `${user.fullName}'s account has been reactivated.`,
      });
    } catch (err) {
      console.error('❌ [SuperAdminPage] Reactivate user error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not reactivate user account.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

// ========================================
  // SORTING & FILTERING HANDLERS
  // ========================================
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field - default to desc
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page
  };

  const handleRoleFilterChange = (value) => {
    setFilterRole(value);
    setCurrentPage(1); // Reset to first page
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
    setCurrentPage(1); // Reset to first page
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'default';
      case 'MODERATOR':
        return 'secondary';
      case 'DEALER':
        return 'default';
      case 'REAL_ESTATE_AGENT':
        return 'default';
      case 'USER':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'default';
      case 'PENDING_APPROVAL':
        return 'secondary';
      case 'SUSPENDED':
        return 'destructive';
      case 'REJECTED':
        return 'outline';
      default:
        return 'outline';
    }
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-6">
      {/* PENDING APPROVALS SECTION */}
      {(loadingApprovals || pendingUsers.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Pending Account Approvals
            </CardTitle>
            <CardDescription>
              Review and approve new user registrations
            </CardDescription>
          </CardHeader>

          {loadingApprovals ? (
            <ApprovalsSkeleton />
          ) : pendingUsers.length === 0 ? (
            <CardContent>
              <p className="text-center text-muted-foreground py-8">
                No pending approvals
              </p>
            </CardContent>
          ) : (
            <CardContent>
              <div className="space-y-3">
                {pendingUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      updatingId === user.id ? 'opacity-50' : ''
                    }`}
                  >
                    <div>
                      <p className="font-medium">{user.fullName}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Registered: {format(new Date(user.createdAt), 'PPP')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {updatingId === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleApproveUser(user.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openRejectDialog(user)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* EXISTING USERS MANAGEMENT SECTION */}
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>Manage user roles and permissions</CardDescription>
        </CardHeader>

<CardContent className="space-y-4">
          {/* Filters Row */}
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filter by Role</label>
              <Select value={filterRole} onValueChange={handleRoleFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="DEALER">Dealer</SelectItem>
                  <SelectItem value="REAL_ESTATE_AGENT">Real Estate Agent</SelectItem>
                  <SelectItem value="MODERATOR">Moderator</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filter by Status</label>
              <Select value={filterStatus} onValueChange={handleStatusFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="PENDING_APPROVAL">Pending</SelectItem>
                  <SelectItem value="SUSPENDED">Suspended</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Total Users</label>
              <div className="h-10 px-3 border rounded-md flex items-center bg-muted">
                <span className="font-semibold">{totalUsers}</span>
              </div>
            </div>
          </div>
        </CardContent>

        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <CardContent>
            <p className="text-destructive">Error loading users: {error}</p>
          </CardContent>
        ) : (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSortChange('role')}
                      className="hover:bg-transparent"
                    >
                      Role
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSortChange('accountStatus')}
                      className="hover:bg-transparent"
                    >
                      Status
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSortChange('createdAt')}
                      className="hover:bg-transparent"
                    >
                      Joined On
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {users?.map((user) => (
                  <TableRow key={user.id} className={updatingId === user.id ? 'opacity-50' : ''}>
                    <TableCell className="font-medium">{user.fullName || 'N/A'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)} className="capitalize">
                        {user.role?.toLowerCase() || 'user'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(user.accountStatus)} className="capitalize">
                        {user.accountStatus?.toLowerCase().replace('_', ' ') || 'pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.createdAt ? format(new Date(user.createdAt), 'PPP') : 'N/A'}
                    </TableCell>
                    <TableCell className="text-center">
                      {updatingId === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() =>
                                toast({
                                  title: 'Not Implemented',
                                  description: 'Viewing user activity is not yet implemented.',
                                })
                              }
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Activity
                            </DropdownMenuItem>
                            
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Account Status</DropdownMenuLabel>
                            
                            {user.accountStatus === 'APPROVED' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleSuspendUser(user)}
                                  className="text-orange-600"
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Suspend Account
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleBanUser(user)}
                                  className="text-red-600"
                                >
                                  <Ban className="mr-2 h-4 w-4" />
                                  Ban Account
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {user.accountStatus === 'SUSPENDED' && (
                              <DropdownMenuItem
                                onClick={() => handleReactivateUser(user)}
                                className="text-green-600"
                              >
                                <UserPlus className="mr-2 h-4 w-4" />
                                Reactivate Account
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Change Role</DropdownMenuLabel>
                            <DropdownMenuItem
                              disabled={user.role === 'USER'}
                              onClick={() => handleRoleChange(user.id, 'USER')}
                            >
                              Make User
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={user.role === 'DEALER'}
                              onClick={() => handleRoleChange(user.id, 'DEALER')}
                            >
                              Make Dealer
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={user.role === 'REAL_ESTATE_AGENT'}
                              onClick={() => handleRoleChange(user.id, 'REAL_ESTATE_AGENT')}
                            >
                              Make Real Estate Agent
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={user.role === 'MODERATOR'}
                              onClick={() => handleRoleChange(user.id, 'MODERATOR')}
                            >
                              Make Moderator
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={user.role === 'ADMIN'}
                              onClick={() => handleRoleChange(user.id, 'ADMIN')}
                            >
                              Make Admin
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {users?.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No users found.</p>
            )}

            {/* PAGINATION */}
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalUsers}
                itemsPerPage={20}
                showInfo={true}
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* REJECT/BAN USER DIALOG */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.accountStatus === 'PENDING_APPROVAL' ? 'Reject User Account' : 'Ban User Account'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to {selectedUser?.accountStatus === 'PENDING_APPROVAL' ? 'reject' : 'ban'} {selectedUser?.fullName}'s account?
              You can optionally provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Reason for rejection/ban (optional)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setSelectedUser(null);
                setRejectionReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={selectedUser?.accountStatus === 'PENDING_APPROVAL' ? handleRejectUser : handleBanUserConfirm}
              disabled={updatingId === selectedUser?.id}
            >
              {updatingId === selectedUser?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              {selectedUser?.accountStatus === 'PENDING_APPROVAL' ? 'Reject Account' : 'Ban Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}